import React, { type Dispatch, type SetStateAction, type SyntheticEvent, useEffect, useRef } from "react";
import { ItemsTypes, RouteNames, SortingDirection, SortOptions, ViewType } from "enums";
import type { AccountPack } from "features/Accounts/types";
import type { SearchState } from "features/Application/slices/createSearchSlice";
import columnOptions, { columnsMap } from "features/Accounts/EditAccount/EditPacks/AccountPacksList/columnOptions";
import { isEmpty, isNil } from "lodash";
import { Table, type DropdownProps } from "semantic-ui-react";
import { NoResults, NoSearchResults, PackTitleCell, SearchInput } from "components";
import { gridSortingOptions } from "features/Accounts/EditAccount/EditPacks/AccountPacksList/sortOptions";
import { ItemsView } from "views";
import dateTimeUtils from "utils/dateTimeUtils";
import cn from "classnames";
import { ExpirationCell } from "features/Accounts/EditAccount/EditPacks/AccountPacksList/ExpirationCell/ExpirationCell";
import type { CardsViewerItem } from "components/cardsViewer/types";
import styles from "./BasePacksTable.module.scss";
import AccountPackCard from "./AccountPackCard/AccountPackCard";

export type BasePacksTableProps = {
  selectedViewType: ViewType;
  packsGrid: {
    items: Array<AccountPack>;
    isLoading: boolean;
    itemsCount: number;
    areAllLoaded: boolean;
  };
  packsList: {
    items: Array<AccountPack>;
    isLoading: boolean;
    itemsCount: number;
  };
  loadGridItems: (skip: number, order: string, appliedFilter: any, searchTerm?: string) => any;
  loadListItems: (skip: number, top: number, order: string, appliedFilter: any, searchTerm?: string) => void;
  searchState?: SearchState;
  resetGridItems: () => void;
  resetListItems: () => void;
  setSearch: (term: string) => { payload: string; type: string };
  setSelectedViewType: Dispatch<SetStateAction<ViewType>>;
  hasPurchasePower: boolean;
  isAccountHome: boolean;
  moboAccountId: number;
  accountId: number;

  viewType?: ViewType;
  setViewType?: (viewType: ViewType) => void;
  canAddPack?: boolean;
  renderAddPacksButton?: () => React.ReactElement;
  renderActionCell?: (pack: AccountPack) => React.ReactNode;
  listViewRtnEvents?: string[];
  triggerRefreshPacksGrid?: (viewType: ViewType) => void;

  cardActionHandlers?: {
    onEditLicensing?: (id: number) => void;
    onExtendLicenses?: (id: number, title: string, thumbnailImageUrl: string) => void;
    onRemovePackFromAccount?: (id: number) => void;
    isAllowedToRemovePackFromAccount?: () => boolean;
    onArchivePack?: (id: number) => void;
    onRestorePack?: (id: number, title: string, thumbnailImageUrl: string) => void;
    onConvertTrialLicense?: (id: number) => void;
    onTogglePaymentModal?: (pack: AccountPack) => void;
  };
  hasPurchasePackAddon: boolean;
};

export const BasePacksTable = ({
  accountId,
  canAddPack,
  cardActionHandlers,
  hasPurchasePower,
  isAccountHome,
  listViewRtnEvents,
  loadGridItems,
  loadListItems,
  moboAccountId,
  packsGrid,
  packsList,
  renderActionCell,
  renderAddPacksButton,
  resetGridItems,
  resetListItems,
  searchState,
  selectedViewType,
  setSearch,
  setSelectedViewType,
  hasPurchasePackAddon,
}: BasePacksTableProps) => {
  const [gridOrderBy, setGridOrderBy] = React.useState(SortOptions.Title);
  const [gridDataRequest, setGridDataRequest] = React.useState<any>();
  const reloadListItemsRef = useRef<() => void>();

  const defaultSortingColumnName = "Date Added";

  const getPacksGridItems = () => {
    gridDataRequest && gridDataRequest.abort();
    const loadedPacksCount = packsGrid.items.length;
    const request = loadGridItems(loadedPacksCount, gridOrderBy, {}, searchState?.term);
    setGridDataRequest(request);
  };

  const getLicensesCount = (pack: AccountPack): string | number => {
    if (pack.hasSource) {
      return "Auto-increment";
    }
    if (pack.isOwn || isNil(pack.licensesCount)) {
      return "Unlimited";
    }
    return pack.licensesCount?.toLocaleString("en-US");
  };

  const viewState = selectedViewType === ViewType.GRID ? packsGrid : packsList;

  const getPacksListItems = (
    skip?: number,
    top?: number,
    sortingColumnName = defaultSortingColumnName,
    sortingDirection = SortingDirection.Descending,
  ) => {
    const orderParams = `${columnsMap[sortingColumnName]} ${sortingDirection}`;
    loadListItems(skip ?? 0, top ?? 10, orderParams, {}, searchState?.term);
  };

  const isFiltered = !isEmpty(searchState?.term);

  const onSortChange = (_ev: SyntheticEvent<HTMLElement>, data: DropdownProps) => {
    resetPacks();
    setGridOrderBy(data.value as SortOptions);
  };

  const resetPacks = async () => {
    const mapResetPacks = {
      [ViewType.GRID]: resetGridItems,
      [ViewType.LIST]: resetListItems,
    };
    return mapResetPacks[selectedViewType]();
  };

  const onViewTypeChange = (viewType: ViewType) => {
    resetPacks();
    setSelectedViewType(viewType);
  };

  const onSearchChange = (search: string) => {
    resetPacks();
    setSearch(search);
    reloadListItemsRef.current?.();
  };

  const noResultsContent = () =>
    isFiltered ? (
      <NoSearchResults />
    ) : (
      <NoResults
        title="No packs for this account"
        description={
          <span>
            Looks like this account doesn't have any packs yet.
            <br />
            {canAddPack && "Add some now using the button below."}
          </span>
        }
        iconClassName="fa-box-full"
      >
        {canAddPack && renderAddPacksButton && renderAddPacksButton()}
      </NoResults>
    );

  const renderCard = (pr: CardsViewerItem<AccountPack>) => {
    return (
      <AccountPackCard
        hasPurchasePower={hasPurchasePower}
        isAccountHome={isAccountHome}
        url={`/${RouteNames.purchasedPacks}/${pr.item.id}`}
        moboAccountId={moboAccountId !== accountId ? moboAccountId : undefined}
        hasPurchasePackAddon={hasPurchasePackAddon}
        {...pr}
        {...cardActionHandlers}
      />
    );
  };

  const buildTableBody = (pack: AccountPack) => {
    const isOwnPack = pack.isOwn;
    const expirationDate =
      isOwnPack || isNil(pack.expirationDate) ? "Never" : dateTimeUtils.formatDate(pack.expirationDate);
    const readOnlyClass = cn({
      rowDisabled: pack.isArchived,
    });

    return (
      <>
        <Table.Cell width={columnOptions[0].width} className={readOnlyClass}>
          <PackTitleCell
            moboAccountId={moboAccountId !== accountId ? moboAccountId : undefined}
            target={moboAccountId !== accountId ? "_blank" : undefined}
            pack={pack}
            url={`/${RouteNames.purchasedPacks}/${pack.id}`}
          />
        </Table.Cell>
        <Table.Cell width={columnOptions[1].width} className={readOnlyClass}>
          {getLicensesCount(pack)}
        </Table.Cell>
        <Table.Cell width={columnOptions[2].width} className={readOnlyClass}>
          {dateTimeUtils.formatDate(pack.issuedDate)}
        </Table.Cell>
        <ExpirationCell isExpired={pack.isExpired} date={expirationDate} className={readOnlyClass} />
        {renderActionCell && (
          <Table.Cell className={styles["align-right-packs"]} width={columnOptions[4].width}>
            {renderActionCell(pack)}
          </Table.Cell>
        )}
      </>
    );
  };

  useEffect(() => {
    if (selectedViewType === ViewType.GRID) {
      getPacksGridItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedViewType, searchState, gridOrderBy]);

  useEffect(() => {
    return () => {
      resetListItems();
      resetGridItems();
      setSearch("");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSetReloadListItems = (callback: () => void) => {
    reloadListItemsRef.current = callback;
  };

  return (
    <ItemsView
      className={styles["account-packs-list"]}
      viewType={selectedViewType}
      renderCard={renderCard}
      columnOptions={columnOptions}
      getData={selectedViewType === ViewType.GRID ? getPacksGridItems : getPacksListItems}
      itemsInlineCount={viewState.itemsCount}
      isLoading={viewState.isLoading}
      items={viewState.items}
      isAllDataLoaded={packsGrid.areAllLoaded}
      itemsType={ItemsTypes.Pack}
      buildTableBody={(pack: AccountPack) => buildTableBody(pack)}
      sortingColumnName={defaultSortingColumnName}
      sortingDirection={SortingDirection.Descending}
      noResultsContent={noResultsContent()}
      withSelection={false}
      renderSearch={() => (
        <SearchInput placeholder="Search for packs..." onChange={onSearchChange} defaultValue={searchState?.term} />
      )}
      setReloadListItems={onSetReloadListItems}
      onViewTypeChange={onViewTypeChange}
      sortOptions={gridSortingOptions}
      onSortChange={onSortChange}
      doNotLoadPersistentViewType
      listViewRtnEvents={listViewRtnEvents}
    />
  );
};
